import { Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { BankStore, ContactStore } from '../core/stores/base.store';
import { UtilityService } from '../core/services/utility.service';
import { Bank, Loan } from '../core/models';
import { FuseConfirmationService } from '../core/services/confirmation/confirmation.service';
import { LoanUtils } from '../core/utils';
import { RegisterComponent } from './register/register.component';
import { FundLocDialogComponent } from './fund-loc-dialog/fund-loc-dialog.component';
import { LoanPaymentDialogComponent } from './loans/loan-payment-dialog/loan-payment-dialog.component';
import { formatDateAsYYYYMMDD } from 'app/core/utils/date.utils';

@Component({
  selector: 'app-bank',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSidenavModule,
    MatListModule,
    MatSortModule,
    MatTooltipModule,
    RegisterComponent,
    FundLocDialogComponent,
    LoanPaymentDialogComponent
  ],
  templateUrl: './bank.component.html',
  styleUrls: ['./bank.component.css']
})
export class BankComponent {
  bankStore = inject(BankStore);
  contactStore = inject(ContactStore);
  private router = inject(Router);
  private _fuseConfirmationService = inject(FuseConfirmationService);
  private dialog = inject(MatDialog);

  utilityService = inject(UtilityService);

  dataSource = computed(() => this.bankStore.entities());
  loading = computed(() => this.bankStore.loading());
  states = ['active', 'paid_off', 'defaulted', 'pending']

  displayedColumns: string[] = [
    'loanName',
    'contact',
    'closeDate',
    'amount',
    'payment',
    'balance',
    'VRR',
    'options'
  ];
  
  constructor() {
    // Select the first bank by default when banks are loaded
    effect(() => {
      const banks = this.dataSource();
      if (banks.length > 0 && !this.bankStore.selected()) {
        this.bankStore.selectEntity(banks[0]);
      }
    });
  }

  addLoan() {
    if (this.bankStore.selected()) {
      this.bankStore.selectLoan(null)
      this.bankStore.selectEntity(this.bankStore.selected());
      this.router.navigate(['/loans/add']);
    }
  }

  deleteLoan(loan: any): void {
    const confirmation = this._fuseConfirmationService.open({
      title: 'Delete Loan',
      message: 'Are you sure you want to delete this loan? This action cannot be undone.',
      actions: {
        confirm: {
          label: 'Delete'
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      // Handle both formats: string 'confirmed' (legacy) or object with result property (new)
      const isConfirmed = result === 'confirmed' || 
                         (result && typeof result === 'object' && result.result === 'confirmed');
                         
      if (isConfirmed) {
        this.bankStore.deleteLoan({loan:loan, endpoint: 'loan', collectionName: 'loans_collection'});
      }
    });
  }

  makeLocTransaction() {
    // Navigate to a transaction form or open a dialog for LOC transactions
    this.router.navigate(['/banks', this.bankStore.selected()?.id, 'loc-transaction']);
  }
  
  selectBank(bank: Bank) {
    this.bankStore.selectEntity(bank);
  }

  calculateRemainingBalance(loan: any): number {
    // TODO: Implement loan balance calculation
    return loan.amount || 0;
  }

  velocity(loan: any): string {
    if (!loan || !loan.amount || !loan.payment || !loan.term || !loan.next_payment_date || !loan.close_date) {
      return '0%';
    }
    
    try {
      // Loan term is already in months in the database
      const termMonths = Number(loan.term);
      const startDate = new Date(loan.next_payment_date);
      const closeDate = new Date(loan.close_date);
      
      // Calculate VRR using LoanUtils
      const vrr = LoanUtils.calculateVRR(
        Number(loan.amount),
        Number(loan.payment),
        termMonths,
        startDate,
        closeDate,
        loan.frequency || 'monthly'
      );
            
      // Format as percentage with 2 decimal places
      return vrr.toFixed(2) + '%';
    } catch (error) {
      console.error('Error calculating VRR:', error);
      return '0%';
    }
  }

  getVRRTooltip(loan: any): string {
    if (!loan || !loan.amount || !loan.payment || !loan.term) {
      return 'Insufficient data to calculate VRR';
    }
    
    const principal = Number(loan.amount);
    const payment = Number(loan.payment);
    const termMonths = Number(loan.term);
    const paymentsPerYear = LoanUtils.getPaymentsPerYear(loan.frequency || 'monthly');
    const totalPayments = Math.ceil((termMonths / 12) * paymentsPerYear);
    const totalPaymentAmount = payment * totalPayments;
    const profit = totalPaymentAmount - principal;
    
    const startDate = new Date(loan.next_payment_date);
    const closeDate = new Date(loan.close_date);
    
    // Ensure we use the earlier date as the start date and the later date as the close date
    const earlierDate = startDate < closeDate ? startDate : closeDate;
    const laterDate = startDate > closeDate ? startDate : closeDate;
    
    const durationMs = laterDate.getTime() - earlierDate.getTime();
    const durationYears = Math.max(durationMs / (1000 * 60 * 60 * 24 * 365.25), 1/365.25);
    
    const vrr = (profit / principal) / durationYears * 100;
    
    return `VRR Calculation:
Principal: $${principal.toFixed(2)}
Payment: $${payment.toFixed(2)}
Term: ${termMonths} months
Total Payments: ${totalPayments}
Total Amount Paid: $${totalPaymentAmount.toFixed(2)}
Profit: $${profit.toFixed(2)}
Start Date: ${earlierDate.getMonth() + 1}/${earlierDate.getDate()}/${earlierDate.getFullYear()}
End Date: ${laterDate.getMonth() + 1}/${laterDate.getDate()}/${laterDate.getFullYear()}
Duration: ${durationYears.toFixed(2)} years
VRR: ${vrr.toFixed(2)}%`;
  }

  num_loans(loans: Loan[]): string {
    if (loans.length == 1)
      return '1 loan';
    return String(loans.length) + ' loans';
  }

  get_name(contactId: number): string {
    if (!this.contactStore.entities()) return '';
    
    const contact = this.contactStore.entities().find(c => c.id == contactId);
    if (!contact) return 'Unknown Contact';
    
    return contact.first_name + ' ' + contact.last_name;
  }

  formatCurrency(amount: number | null): string {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
  }

  addBank() {
    this.bankStore.selectEntity(null);
    this.router.navigate(['/banks/add']);
  }

  editBank() {
    this.router.navigate(['/banks/edit']);
  }

  editLoan(loan: Loan) {
    // Select the loan in the store
    this.bankStore.selectLoan(loan);
    // Navigate to the edit loan page
    this.router.navigate(['/loans/edit']);
  }

  viewAmortization(loan: Loan) {
    this.bankStore.selectLoan(loan);
    this.router.navigate([`/loans/amortization`]);
  }

  viewDocumentation(loan: Loan) {
    this.bankStore.selectLoan(loan);
    this.router.navigate([`/loans/documentation`]);
  }

  disableLoc() {
    const confirmation = this._fuseConfirmationService.open({
      title: 'Disable Line of Credit',
      message: 'Are you sure you want to disable this line of credit? This action cannot be undone!',
      icon: {
        show: true,
        name: 'warning',
        color: 'warn'
      },
      actions: {
        confirm: {
          label: 'Disable',
          color: 'warn'
        },
        cancel: {
          label: 'Cancel',
          show: true
        }
      }
    })

    confirmation.afterClosed().subscribe((result) => {
      // Handle both formats: string 'confirmed' (legacy) or object with result property (new)
      const isConfirmed = result === 'confirmed' || 
                         (result && typeof result === 'object' && result.result === 'confirmed');
                         
      if (isConfirmed) {
        let bank = this.bankStore.selected();
        delete bank.loans_collection
        delete bank.bank_transactions_collection
        bank.starting_balance = Number(bank.starting_balance);
        bank.loc_balance = Number(bank.loc_balance);
        bank.loc_total_interest = 0
        bank.loc = false
        this.bankStore.update(bank);
      }
    })
  }

  /**
   * Opens the LOC payment dialog to make a payment to the Personal Line of Credit
   */
  fundLOC(): void {
    const selectedBank = this.bankStore.selected();
    if (!selectedBank || !selectedBank.loc) {
      return;
    }

    const dialogRef = this.dialog.open(FundLocDialogComponent, {
      width: '500px',
      data: { bank: selectedBank }
    });
  }

  /**
   * Opens the loan payment dialog to record a payment for a loan
   */
  recordLoanPayment(loan: Loan): void {
    const selectedBank = this.bankStore.selected();
    if (!selectedBank) {
      return;
    }

    // Create a deep copy of the loan to avoid reference issues
    // Ensure numeric values are properly preserved
    const loanCopy = {
      ...loan,
      // Ensure these are numbers
      payment: typeof loan.payment === 'number' ? loan.payment : parseFloat(String(loan.payment)),
      amount: typeof loan.amount === 'number' ? loan.amount : parseFloat(String(loan.amount)),
      interest_rate: typeof loan.interest_rate === 'number' ? loan.interest_rate : parseFloat(String(loan.interest_rate)),
      remaining_balance: typeof loan.remaining_balance === 'number' ? loan.remaining_balance : parseFloat(String(loan.remaining_balance)),
      next_payment_number: typeof loan.next_payment_number === 'number' ? loan.next_payment_number : parseFloat(String(loan.next_payment_number))
    };
        
    const dialogRef = this.dialog.open(LoanPaymentDialogComponent, {
      width: '500px',
      data: { loan: loanCopy, bank: selectedBank }
    });

    // Handle the dialog close event
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.transaction) {
          // Ensure the transaction date is in the correct format
          const transaction = {
            ...result.transaction,
            transaction_date: formatDateAsYYYYMMDD(result.transaction.transaction_date)
          };
          
          this.bankStore.createChild({
            child: transaction,
            endpoint: 'bank_transaction',
            collectionName: 'bank_transactions_collection'
          });
        }
        
        if (result.updatedLoan) {
          // Ensure all dates in the loan are in the correct format
          const loan = {
            ...result.updatedLoan,
            next_payment_date: formatDateAsYYYYMMDD(result.updatedLoan.next_payment_date),
          };
          
          this.bankStore.updateLoan({
            loan,
            endpoint: 'loan',
            collectionName: 'loans_collection'
          });
          
          // Force a refresh of the selected loan to ensure the UI updates if this loan is selected
          if (this.bankStore.selectedLoan()?.id === loan.id) {
            this.bankStore.selectLoan(null);
            setTimeout(() => {
              this.bankStore.selectLoan(loan);
            }, 0);
          }
        }
      }
    });
  }
}
